import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  formatSortParam,
  parseSortParam,
  setOrDeleteParam,
  TableSort,
} from "./listUrlParams.ts";

export function useTableUrlState<Field extends string, Filters>({
  sortFields,
  defaultSort,
  parseFilters,
  writeFilters,
}: {
  sortFields: readonly Field[];
  defaultSort: TableSort<Field>;
  parseFilters: (params: URLSearchParams) => Filters;
  writeFilters: (params: URLSearchParams, filters: Filters) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();

  const sort = useMemo(
    () => parseSortParam(searchParams.get("sort"), sortFields) ?? defaultSort,
    [searchParams, sortFields, defaultSort],
  );
  const filters = useMemo(
    () => parseFilters(searchParams),
    [searchParams, parseFilters],
  );

  const updateParams = useCallback(
    (update: (params: URLSearchParams) => void) =>
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          update(params);
          return params;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const setSort = useCallback(
    (nextSort: TableSort<Field>) =>
      updateParams((params) =>
        setOrDeleteParam(params, "sort", formatSortParam(nextSort)),
      ),
    [updateParams],
  );

  const setFilters = useCallback(
    (nextFilters: Filters) =>
      updateParams((params) => writeFilters(params, nextFilters)),
    [updateParams, writeFilters],
  );

  return { sort, filters, setSort, setFilters };
}
